import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { matchPath, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { getExpiringCredentialsCounts } from '@Helpers';

import { ErrorMessage, FullPageLoading, MessagePopup } from '@Components';
import { SidebarInset, SidebarProvider, SidebarTrigger } from '@Components/ui-v2/sidebar';
import { ClientConfiguration, ExpiringCredential } from '@Models';
import { AppDispatch, clientOperations, RootState } from '@Store';
import EmptyState from '../../Pages/EmptyState/emptyState';
import SmbUnauthorized from '../../Pages/SmbUnauthorized/smbUnauthorized';
import { AgentRoot } from '../Agents';
import PlatformSidebar from '../PlatformSidebar/platformSidebar.container';

// Every flavor now uses the light sidebar. The `.sidebar-dark` block stays in
// app.scss as parked theme tokens in case we want to bring the dark variant
// back, but no one applies it today.
const sidebarThemeClass: string | undefined = undefined;

const ERROR_INVALID_MEMBERSHIP = 'Invalid membership';
const ERROR_INVALID_CLIENT = 'Invalid client';

interface WithSidebarProps {
  configuration: ClientConfiguration;
  isFetching: boolean;
  error: string;
  fetchClientConfiguration: () => void;
  expiresCredentials?: ExpiringCredential[];
}

const WithSidebar = ({
  configuration,
  isFetching,
  error,
  fetchClientConfiguration,
  expiresCredentials,
}: WithSidebarProps) => {
  const currentPath = useLocation().pathname;
  const { t } = useTranslation();
  const navigate = useNavigate();

  useEffect(() => {
    if (!configuration && !error) fetchClientConfiguration();
  }, [configuration, error, fetchClientConfiguration]);

  if (isFetching || (!configuration && !error)) return <FullPageLoading />;

  // GS1 users without the required membership are rejected by the backend
  // (403 "Invalid membership") and land on the standalone SmbUnauthorized
  // page — rendered full-screen, outside the sidebar shell.
  const isSmbUnauthorized = error && error.includes(ERROR_INVALID_MEMBERSHIP);
  if (isSmbUnauthorized) return <SmbUnauthorized />;

  const hasNoClient = error && error.includes(ERROR_INVALID_CLIENT);
  if (hasNoClient) {
    return (
      <SidebarProvider className={sidebarThemeClass}>
        <PlatformSidebar locked />
        <SidebarInset>
          <EmptyState />
        </SidebarInset>
      </SidebarProvider>
    );
  }

  const titleMap = [
    { label: t('Sidebar.Dashboard'), link: '/recommendation' },
    { label: t('Sidebar.Monitor'), link: '/monitor' },
    { label: t('Sidebar.SecondaryMonitor'), link: '/monitor2' },
    { label: t('Sidebar.Configure'), link: '/configure' },
  ];
  const title = titleMap.find((entry) => matchPath(currentPath, entry.link))?.label ?? '';

  const { popupMessage } = configuration || {};

  const credentialsMessage = (() => {
    const list = expiresCredentials ?? [];
    if (list.length === 0) return undefined;
    const { expiredCount, expiringCount } = getExpiringCredentialsCounts(list);
    const prefix =
      expiredCount > 0
        ? t('Credentials.ExpiredPopupPrefix', { count: expiredCount })
        : t('Credentials.ExpiringPopupPrefix', { count: expiringCount });
    return (
      <>
        {prefix}. {t('Credentials.UpdateMessage')}{' '}
        <span
          role="button"
          tabIndex={0}
          className="cursor-pointer text-brand-600 underline"
          onClick={() => navigate('/credentials')}>
          {t('Credentials.Here')}
        </span>
        .
      </>
    );
  })();

  return (
    <AgentRoot>
      <SidebarProvider className={sidebarThemeClass}>
        <PlatformSidebar />
        <SidebarInset className="flex flex-col overflow-y-auto">
          {error && <ErrorMessage internalMessage={`Error fetching client configuration: ${error}`} />}
          {!error && (
            <>
              {(popupMessage || credentialsMessage) && (
                <MessagePopup
                  message={
                    <>
                      {popupMessage}
                      {popupMessage && credentialsMessage ? <br /> : null}
                      {credentialsMessage}
                    </>
                  }
                  storageKey="popupMessage.dismissed"
                />
              )}
              <div className="flex items-center gap-2 p-2 md:hidden">
                <SidebarTrigger />
                {title && <h2 className="text-base font-semibold">{title}</h2>}
              </div>
              <Outlet />
            </>
          )}
        </SidebarInset>
      </SidebarProvider>
    </AgentRoot>
  );
};

const mapStateToProps = (state: RootState) => {
  const { client, credentials } = state;
  return {
    configuration: client.configuration,
    isFetching: client.isFetching,
    error: client.error,
    expiresCredentials: (credentials?.expires_credentials ?? []) as ExpiringCredential[],
  };
};

const mapDispatchToProps = (dispatch: AppDispatch) => ({
  fetchClientConfiguration: () => dispatch(clientOperations.fetchClientConfiguration()),
});

export default connect(mapStateToProps, mapDispatchToProps)(WithSidebar);
